"use client";

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import {
  subscribeWallet,
  getOrCreateWallet,
  type UserWallet,
} from '@/services/wallet';

// ═══════════════════════════════════════════════════════════════════════════════
// Context Types
// ═══════════════════════════════════════════════════════════════════════════════

interface WalletContextValue {
  wallet: UserWallet | null;
  loading: boolean;
  isOnboarded: boolean;
  balance: { available: number; pending: number; lifetime: number };

  /** Start Stripe Express onboarding — opens in new tab */
  startOnboarding: () => Promise<void>;

  /** Request a manual payout */
  requestPayout: (amount?: number) => Promise<{ success: boolean; error?: string }>;

  /** Refresh wallet state */
  refresh: () => Promise<void>;
}

const WalletContext = createContext<WalletContextValue>({
  wallet: null,
  loading: true,
  isOnboarded: false,
  balance: { available: 0, pending: 0, lifetime: 0 },
  startOnboarding: async () => {},
  requestPayout: async () => ({ success: false }),
  refresh: async () => {},
});

// ═══════════════════════════════════════════════════════════════════════════════
// Provider
// ═══════════════════════════════════════════════════════════════════════════════

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const [wallet, setWallet] = useState<UserWallet | null>(null);
  const [loading, setLoading] = useState(true);

  // Subscribe to wallet changes
  useEffect(() => {
    if (!user?.uid) {
      setWallet(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Ensure wallet doc exists
    getOrCreateWallet(user.uid).catch(console.error);

    // Real-time listener
    const unsub = subscribeWallet(user.uid, (w) => {
      setWallet(w);
      setLoading(false);
    });

    return unsub;
  }, [user?.uid]);

  /** Ask the server to verify our Connect account's real status with Stripe
   *  and sync it to the wallet. Authoritative — does not depend on the
   *  account.updated webhook having been delivered. */
  const syncStripeStatus = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/stripe-connect/status', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => null);
      if (data?.onboardingComplete) {
        const w = await getOrCreateWallet(user.uid);
        setWallet(w);
      }
    } catch { /* non-fatal — webhook remains as backup */ }
  }, [user]);

  // Handle Stripe onboarding return
  useEffect(() => {
    if (typeof window === 'undefined' || !user?.uid) return;
    const params = new URLSearchParams(window.location.search);
    const stripeReturn = params.get('stripe_return');

    if (stripeReturn === 'true') {
      // Verify the REAL status with Stripe. Returning from the Stripe flow does
      // not mean onboarding finished — the user may have abandoned it — so
      // completion is only ever set from Stripe's own account state (status
      // route or webhook). The account id itself is recorded by the server
      // (/api/stripe-connect/onboard); the browser never writes or trusts it.
      void syncStripeStatus();
      // Clean URL params
      const url = new URL(window.location.href);
      url.searchParams.delete('stripe_return');
      url.searchParams.delete('account_id');   // legacy links
      window.history.replaceState({}, '', url.toString());
    }
  }, [user?.uid, syncStripeStatus]);

  // Self-heal: wallet has an account id but isn't marked complete — the
  // webhook may have been missed. Verify with Stripe once per session.
  const statusSyncedRef = useRef(false);
  useEffect(() => {
    if (statusSyncedRef.current) return;
    if (!user?.uid || !wallet?.stripeConnectAccountId || wallet?.stripeOnboardingComplete) return;
    statusSyncedRef.current = true;
    void syncStripeStatus();
  }, [user?.uid, wallet?.stripeConnectAccountId, wallet?.stripeOnboardingComplete, syncStripeStatus]);

  const startOnboarding = useCallback(async () => {
    if (!user?.uid) return;
    // Email is required by Stripe. Fall back to the profile email if the Firebase
    // user record has none (some sign-in methods don't expose it).
    const email = user.email || (userProfile as any)?.email || '';
    if (!email) {
      toast({ title: 'Add an email first', description: 'Your account has no email on file, which Stripe needs for payouts. Add one in Settings, then try again.', variant: 'destructive' });
      return;
    }

    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/stripe-connect/onboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId: user.uid, email, returnUrl: window.location.origin }),
      });

      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || `Onboarding failed (${res.status})`);

      // Redirect the CURRENT tab to Stripe. window.open('_blank') after an await
      // is treated as non-user-initiated and gets popup-blocked (which is why
      // "Set Up Now" appeared to do nothing). Stripe's onboarding is a redirect
      // flow — it returns to /wallet?stripe_return=… when done.
      if (data.onboardingUrl) {
        window.location.href = data.onboardingUrl;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err: any) {
      console.error('Failed to start Stripe onboarding:', err);
      toast({ title: 'Could not start payout setup', description: err?.message || 'Please try again in a moment.', variant: 'destructive' });
    }
  }, [user, userProfile, toast]);

  const requestPayout = useCallback(async (amount?: number) => {
    if (!wallet?.stripeConnectAccountId) {
      return { success: false, error: 'No connected account' };
    }
    if (!user) {
      return { success: false, error: 'Not signed in' };
    }

    try {
      // The payout route verifies the caller owns this account (or is an
      // admin) — the ID token is required. The old call sent no token at
      // all against an admin-only gate, so every payout click failed 401.
      const token = await user.getIdToken();
      const res = await fetch('/api/stripe-connect/payout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          stripeConnectAccountId: wallet.stripeConnectAccountId,
          amount,
          currency: wallet.defaultCurrency,
        }),
      });

      const data = await res.json();
      if (data.error) return { success: false, error: data.error };
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  }, [user, wallet?.stripeConnectAccountId, wallet?.defaultCurrency]);

  const refresh = useCallback(async () => {
    if (!user?.uid) return;
    const w = await getOrCreateWallet(user.uid);
    setWallet(w);
  }, [user?.uid]);

  const isOnboarded = wallet?.stripeOnboardingComplete ?? false;
  const balance = wallet?.balances ?? { available: 0, pending: 0, lifetime: 0 };

  return (
    <WalletContext.Provider value={{
      wallet,
      loading,
      isOnboarded,
      balance,
      startOnboarding,
      requestPayout,
      refresh,
    }}>
      {children}
    </WalletContext.Provider>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════════════════════

export function useWallet() {
  return useContext(WalletContext);
}
